import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { getBrandIdentity, getWidgetKey } from '../../services/configService';

const WhatsAppLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25d366" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const INTEGRATIONS = [
  {
    id: 'website',
    label: 'Website',
    desc: 'Embed the Pulse chat widget on your website.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    desc: 'Connect Pulse to your WhatsApp Business account.',
    icon: <WhatsAppLogo size={20} />,
  },
];

const slideIn  = { initial: { opacity: 0, x: 16 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -16 } };
const slideOut = { initial: { opacity: 0, x: -16 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 16 } };

function buildSnippet({ brandColor, assistantName, welcomeMessage, darkMode, widgetKey }) {
  return [
    `<script`,
    `  src="https://www.reanoai.com/pulse-widget-v2.js"`,
    brandColor     ? `  data-brand-color="${brandColor}"` : null,
    assistantName  ? `  data-assistant-name="${assistantName}"` : null,
    welcomeMessage ? `  data-welcome-message="${welcomeMessage}"` : null,
    darkMode       ? `  data-theme="dark"` : null,
    widgetKey      ? `  data-api-key="${widgetKey}"` : null,
    `></script>`,
  ].filter(Boolean).join('\n');
}

function WebsiteConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getBrandIdentity(),
      getWidgetKey(),
    ]).then(([identity, keyData]) => {
      setConfig({
        brandColor:     identity.config?.brandColor     || '',
        assistantName:  identity.config?.assistantName  || '',
        welcomeMessage: identity.config?.welcomeMessage || '',
        darkMode:       identity.config?.darkMode ?? false,
        widgetKey:      keyData.widgetKey || keyData.key || '',
      });
    }).catch(() => {
      // show snippet with empty fields on error
      setConfig({ brandColor: '', assistantName: '', welcomeMessage: '', darkMode: false, widgetKey: '' });
    }).finally(() => setLoading(false));
  }, [user]);

  function handleCopy() {
    if (!config) return;
    navigator.clipboard.writeText(buildSnippet(config)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const snippet = config ? buildSnippet(config) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        padding: '12px 16px', borderRadius: 10,
        background: 'rgba(71,158,195,0.06)', border: '1px solid rgba(71,158,195,0.15)',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.65, margin: 0 }}>
          Copy the code below and paste it into your website's <code style={{ background: 'rgba(71,158,195,0.12)', color: '#7dd3fc', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>index.html</code> file, just before the closing <code style={{ background: 'rgba(71,158,195,0.12)', color: '#7dd3fc', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>&lt;/body&gt;</code> tag. Save and publish your website — your chatbot will appear automatically.
        </p>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Embed Snippet</p>
          <button
            onClick={handleCopy}
            disabled={loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 7, border: 'none', cursor: loading ? 'default' : 'pointer',
              background: copied ? 'rgba(16,185,129,0.12)' : 'rgba(71,158,195,0.1)',
              color: copied ? '#10b981' : '#479EC3',
              fontSize: 12.5, fontWeight: 600,
              transition: 'background .2s, color .2s',
            }}
          >
            {copied ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy
              </>
            )}
          </button>
        </div>

        {loading ? (
          <div style={{
            padding: '20px 16px', borderRadius: 10, minHeight: 80,
            background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(71,158,195,0.15)',
            display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
              <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
            </svg>
            Loading configuration…
          </div>
        ) : (
          <textarea
            readOnly
            value={snippet}
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(15,23,42,0.7)',
              border: '1px solid rgba(71,158,195,0.15)',
              color: '#7dd3fc',
              fontSize: 12, fontFamily: "'SF Mono','Fira Code','Cascadia Code',monospace",
              lineHeight: 1.7, outline: 'none', resize: 'none',
              cursor: 'text',
            }}
            onFocus={e => { e.target.style.borderColor = 'rgba(71,158,195,0.4)'; }}
            onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.15)'; }}
          />
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '10px 14px', borderRadius: 9,
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>
          <span style={{ color: '#fbbf24', fontWeight: 600 }}>Tip:</span> Re-copy the snippet after saving changes in Brand Identity or other configuration sections — the widget reads its settings from this embed code.
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const WHATSAPP_NUMBERS = [
  { display: '+91-8708041134', wa: '918708041134' },
  { display: '+91-9896187858', wa: '919896187858' },
];

function WhatsAppConfig() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '16px 18px', borderRadius: 12,
        background: 'rgba(71,158,195,0.06)', border: '1px solid rgba(71,158,195,0.15)',
      }}>
        <div style={{ flexShrink: 0, marginTop: 2 }}><WhatsAppLogo size={18} /></div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>One-click WhatsApp integration coming soon</p>
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65, margin: 0 }}>
            Please reach out to the Reano AI team to get your AI assistant configured on WhatsApp.
          </p>
        </div>
      </div>

      {/* Contact numbers */}
      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Contact us on</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {WHATSAPP_NUMBERS.map(({ display }) => (
            <div key={display} style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '9px 16px', borderRadius: 9, width: 'fit-content',
              background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(71,158,195,0.12)',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span style={{ fontSize: 13.5, color: '#94a3b8', fontFamily: "'SF Mono','Fira Code',monospace", letterSpacing: '0.02em' }}>{display}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p style={{ fontSize: 11.5, color: '#475569', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Email</p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '9px 16px', borderRadius: 9, width: 'fit-content',
          background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(71,158,195,0.12)',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
          <span style={{ fontSize: 13.5, color: '#94a3b8', fontFamily: "'SF Mono','Fira Code',monospace", letterSpacing: '0.02em' }}>support@reanoai.com</span>
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsConfig() {
  const [active, setActive] = useState(null);
  const integration = INTEGRATIONS.find(i => i.id === active);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <AnimatePresence mode="wait">

        {/* List view */}
        {!active && (
          <motion.div key="list" {...slideOut}
            transition={{ duration: 0.18 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>
              Select an integration to configure.
            </p>
            {INTEGRATIONS.map(({ id, label, desc, icon }) => (
              <button key={id} onClick={() => setActive(id)} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '16px 20px', borderRadius: 12, cursor: 'pointer',
                background: 'rgba(15,23,42,0.5)',
                border: '1px solid rgba(71,158,195,0.12)',
                textAlign: 'left', width: '100%',
                transition: 'background .15s, border-color .15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.07)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15,23,42,0.5)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.12)'; }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#479EC3',
                }}>
                  {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 }}>{label}</p>
                  <p style={{ fontSize: 12.5, color: '#64748b' }}>{desc}</p>
                </div>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            ))}
          </motion.div>
        )}

        {/* Detail view */}
        {active && (
          <motion.div key={active} {...slideIn}
            transition={{ duration: 0.18 }}
            style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            {/* Back + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button onClick={() => setActive(null)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#64748b', fontSize: 13, fontWeight: 500, padding: 0,
                transition: 'color .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                Integrations
              </button>
              <span style={{ color: '#334155', fontSize: 13 }}>/</span>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ color: '#479EC3' }}>{integration.icon}</span>
                {integration.label}
              </span>
            </div>

            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 20 }} />

            {active === 'website' ? <WebsiteConfig /> : active === 'whatsapp' ? <WhatsAppConfig /> : null}
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}
